import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import formatRupees from '../../utils/currency';

function CouponModal({
  orderCoupon, coupons, isOpen, onClose, onApplyCoupon,
}) {
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  useEffect(() => {
    if (orderCoupon) {
      setSelectedCoupon(orderCoupon);
    }
  }, [orderCoupon]);
  const now = new Date();
  const enrichedCoupons = coupons?.map((coupon, index) => {
    const expiresAt = new Date(coupon?.expiresAt);
    const isExpired = expiresAt < new Date(now.toDateString());

    return {
      id: index + 1,
      code: coupon?.couponCode,
      expiresAt: coupon?.expiresAt,
      description: coupon?.description ?? 'Get Discounts',
      discount: coupon?.discountType === 'percent' ? `${coupon?.couponValue} %` : formatRupees(coupon?.couponValue),
      maxDiscount: coupon?.discountType === 'percent' ? `${coupon?.maxDiscount}` : '',
      minPurchase: coupon?.minOrderValue,
      validUntil: coupon?.expiresAt,
      discountType: coupon?.discountType,
      isExpired,
      relaxationSplits: coupon?.relaxationSplits ?? [],
    };
  });

  const activeCoupons = enrichedCoupons?.filter((coupon) => !coupon.isExpired);

  const handleApplyCoupon = async () => {
    if (selectedCoupon) {
      onApplyCoupon(selectedCoupon);
      // console.log(selectedCoupon, 'selectedCoupon');
      onClose();
    }
  };

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-end md:items-center justify-center z-50">
      <div
        className="bg-black bg-opacity-50 absolute inset-0 animate-fadeIn delay-100"
        onClick={handleClose}
        tabIndex={0}
        onKeyDown={() => {}}
        role="button"
        aria-label="Close modal"
      />

      <div
        className=" bg-white rounded-t-2xl md:rounded w-full md:max-w-[50%] max-w-[100%] p-4 relative z-10 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
        onKeyDown={() => {}}
        role="button"
      >
        {/* Modal Handle */}
        <div className="md: hidden flex justify-center mb-4">
          <div className="w-16 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="md:p-0 pb-6">
          <h2 className="text-xl font-bold mb-4">Available Coupons</h2>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Active Coupons */}
            {activeCoupons.map((coupon) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <div
                key={coupon.id}
                className={`p-4 border rounded-lg transition-colors ${
                  selectedCoupon?.code === coupon.code
                    ? 'border-[#045692] ring-1 ring-[#045692] bg-blue-50 cursor-pointer'
                    : 'border-gray-300 hover:bg-gray-50 cursor-pointer'
                }`}
                onClick={() => setSelectedCoupon(coupon)}
                role="button"
                tabIndex={0}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-base md:text-lg">{coupon.code}</h3>
                    <p className="text-gray-500 text-xs mt-2">
                      {`Min. purchase: ${formatRupees(coupon?.minPurchase)}`}
                    </p>
                    {coupon?.relaxationSplits?.length > 0 && (
                      <p className="text-gray-500 text-xs mt-1">
                        Applicable on:
                        {' '}
                        <b>
                          {coupon.relaxationSplits
                            .map((split) => split.priceHead.replace('-', ' '))
                            .join(', ')}
                        </b>
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-green-600 font-bold">{coupon.discount}</span>
                    <p className="text-gray-500 text-xs mt-1">
                      Valid till
                      {' '}
                      {new Date(coupon.validUntil).toLocaleDateString()}
                    </p>
                    {coupon?.discountType === 'percent' && (
                      <p className="text-gray-500 text-xs mt-2">
                        {`Max. discount: ${formatRupees(coupon.maxDiscount)}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 mr-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={!selectedCoupon}
              className={`px-4 py-2 rounded-lg ${
                selectedCoupon
                  ? 'bg-[#045692] text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Apply Coupon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

CouponModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApplyCoupon: PropTypes.func.isRequired,
  coupons: PropTypes.array.isRequired,
  orderCoupon: PropTypes.object,
};

export default CouponModal;
